import { FC, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { SidePanel } from '@mews/b2b-ui';
import { ANIMATION_DELAY, Button, Dialog, Divider, Stack } from '@optimus-web/core';
import { lt, showWarningAlert, useDialog, useLocalizeText } from '@mews/framework';
import {
    AgeCategory,
    BookablePeriod,
    OccupancyState,
    Rate,
    Resource,
    ResourceCategory,
    ServiceRatePricing,
    TimeUnitPeriod,
} from '@mews-commander/data-access';
import { GroupOption, Option } from '@mews-commander/types';
import { TextKey } from '@mews-commander/enums';
import { useGainsightCommanderEvents } from '@mews-commander/utils';
import { NewReservationFormKeys, NewReservationFormValues } from '../schema';
import { NewReservationDateTimeBoundaries } from '../types';
import { isRateRestricted } from '../utils/rate';
import { NEW_RESERVATION_BUTTON_DATA_TEST_ID } from '../../../constants';
import { getDepartureDatePickerId, NewReservationFormFields } from './NewReservationFormFields';
import { TotalSummary } from './TotalSummary';

// for now only one space at the time can be selected
const SPACES_COUNT = 1;

export interface NewReservationPanelProps {
    resource: Resource;
    resourceCategory: ResourceCategory;
    ratesById: Record<string, Rate | undefined>;
    timeUnitPeriod: TimeUnitPeriod;
    bookablePeriod: BookablePeriod;
    ageCategories: AgeCategory[];
    canOverbook: boolean;
    overbookingMessage: string | null;
    reservationLengthError: string | null;
    loading: boolean;
    pricingByRateId: Record<string, ServiceRatePricing | undefined> | undefined;
    ratesOptions: Option[] | GroupOption[] | undefined;
    resourceOccupancyStates: OccupancyState[] | undefined;
    dateTimeBoundaries: NewReservationDateTimeBoundaries;
    bookablePeriodsDiffs: Record<BookablePeriod, number>;
    onClose: () => void;
    onSubmit: () => void;
    onDatesChange?: (dates: {
        arrival: { date: string; time: string };
        departure: { date: string; time: string };
    }) => void;
    onAgeCategoriesCounterValuesChange?: (
        ageCategoryCounterValues: NewReservationFormValues[NewReservationFormKeys.AgeCategories]
    ) => void;
}

export const NewReservationPanel: FC<NewReservationPanelProps> = ({
    resource,
    resourceCategory,
    ratesById,
    timeUnitPeriod,
    bookablePeriod,
    ageCategories,
    canOverbook,
    overbookingMessage,
    reservationLengthError,
    loading,
    ratesOptions,
    pricingByRateId,
    resourceOccupancyStates = [],
    dateTimeBoundaries,
    bookablePeriodsDiffs,
    onClose,
    onSubmit,
    onDatesChange,
    onAgeCategoriesCounterValuesChange,
}) => {
    const {
        watch,
        setError,
        clearErrors,
        formState: { isValid, dirtyFields },
    } = useFormContext<NewReservationFormValues>();
    const ltt = useLocalizeText();
    const gainsightEvents = useGainsightCommanderEvents();

    const [overbookingDialogProps, toggleOverbookingDialog] = useDialog();

    const {
        [NewReservationFormKeys.ArrivalDate]: arrivalDate,
        [NewReservationFormKeys.DepartureDate]: departureDate,
        [NewReservationFormKeys.ArrivalHours]: arrivalHours,
        [NewReservationFormKeys.DepartureHours]: departureHours,
        [NewReservationFormKeys.Rate]: rateId,
        [NewReservationFormKeys.AgeCategories]: ageCategoryCounterValues,
    } = watch();

    const previousValues = useRef<NewReservationFormValues[NewReservationFormKeys.AgeCategories]>();

    const showOverbookingError = !canOverbook && !!overbookingMessage;
    const showOverbookingWarning = !!canOverbook && !!overbookingMessage;

    // effect to fire onChange method if some values have changed inside referential stable ageCategoryCounterValues
    useEffect(() => {
        if (!onAgeCategoriesCounterValuesChange) {
            return;
        }

        const valuesChanged =
            !previousValues ||
            Object.entries(ageCategoryCounterValues).some(([id, count]) => previousValues.current?.[id] !== count);

        if (valuesChanged) {
            const newValues = { ...ageCategoryCounterValues };
            previousValues.current = newValues;
            onAgeCategoriesCounterValuesChange(newValues);
        }
    });

    useEffect(() => {
        gainsightEvents.onElementView({ context: 'NewReservationPanel' });
    }, [gainsightEvents]);

    useEffect(() => {
        onDatesChange?.({
            arrival: { date: arrivalDate, time: arrivalHours },
            departure: { date: departureDate, time: departureHours },
        });
    }, [arrivalDate, arrivalHours, departureDate, departureHours, onDatesChange]);

    useEffect(() => {
        if (!showOverbookingError) {
            window.setTimeout(() => {
                document.getElementById(getDepartureDatePickerId(timeUnitPeriod))?.focus();
            }, ANIMATION_DELAY.default);
        }
    }, [resource.id, timeUnitPeriod, showOverbookingError, dirtyFields, clearErrors]);

    const isSpaceBlocked = !!resourceOccupancyStates?.some(isSpaceBlockedOccupancyState);

    useEffect(() => {
        if (showOverbookingError) {
            return setError(NewReservationFormKeys.DepartureDate, { message: overbookingMessage });
        }

        if (reservationLengthError) {
            return setError(NewReservationFormKeys.DepartureDate, { message: reservationLengthError });
        }

        if (isSpaceBlocked) {
            return setError(NewReservationFormKeys.DepartureDate, {
                message: ltt({ textKey: TextKey.NewReservationPanelSpaceBlockedError }),
            });
        }

        clearErrors();
    }, [reservationLengthError, showOverbookingError, overbookingMessage, isSpaceBlocked, setError, clearErrors, ltt]);

    useEffect(() => {
        if (showOverbookingWarning) {
            clearErrors();
            showWarningAlert({ description: <>{overbookingMessage}</> });
        }
    }, [showOverbookingWarning, overbookingMessage, clearErrors]);

    const selectedRate = rateId ? ratesById[rateId] : undefined;
    const selectedRatePrice = rateId && pricingByRateId?.[rateId]?.price;
    const isSelectedRateRestricted = !!selectedRate && isRateRestricted(selectedRate, pricingByRateId ?? {});

    return (
        <>
            <SidePanel
                opened
                title={lt('NewReservation')}
                width="small"
                onClose={onClose}
                headerActions={[]}
                actions={
                    <Stack alignment="end">
                        <Button
                            type="submit"
                            variant="primary"
                            size="medium"
                            data-test-id={NEW_RESERVATION_BUTTON_DATA_TEST_ID}
                            onClick={overbookingMessage ? toggleOverbookingDialog : onSubmit}
                            disabled={
                                !isValid ||
                                !!showOverbookingError ||
                                loading ||
                                !!reservationLengthError ||
                                isSelectedRateRestricted ||
                                ratesOptions?.length === 0 ||
                                isSpaceBlocked
                            }
                        >
                            {lt('Next')}
                        </Button>
                    </Stack>
                }
                content={
                    <Stack vertical spacing="400">
                        <NewReservationFormFields
                            ratesOptions={ratesOptions}
                            rateSelectFieldHelper={selectedRate?.description}
                            loading={loading}
                            timeUnitPeriod={timeUnitPeriod}
                            resource={resource}
                            resourceCategory={resourceCategory}
                            ageCategories={ageCategories}
                            resourceOccupancyStates={resourceOccupancyStates}
                            dateTimeBoundaries={dateTimeBoundaries}
                        />

                        <Divider />

                        {selectedRate && selectedRatePrice && !isSelectedRateRestricted && (
                            <TotalSummary
                                rateName={selectedRate.name}
                                prices={selectedRatePrice}
                                spacesCount={SPACES_COUNT}
                                resourceCategoryType={resourceCategory.type}
                                timeUnitPeriod={timeUnitPeriod}
                                bookablePeriod={bookablePeriod}
                                totalLabel={lt('TimelineNewReservationEstimatedPrice')}
                                bookablePeriodsDiffs={bookablePeriodsDiffs}
                            />
                        )}
                    </Stack>
                }
            />

            <Dialog
                {...overbookingDialogProps}
                data-test-id="overbooking-dialog"
                title={lt('GeneralOverbookingDialogTitle')}
                footer={{
                    primary: (
                        <Button variant="primary" type="submit" onClick={onSubmit}>
                            {lt('Save')}
                        </Button>
                    ),
                    secondary: (
                        <Button variant="tertiary" type="button" onClick={toggleOverbookingDialog}>
                            {lt('GoBack')}
                        </Button>
                    ),
                }}
            >
                {lt('GeneralOverbookingNewReservationDatesConflictDialogDescription', {
                    spaceCategory: resourceCategory.name,
                })}
            </Dialog>
        </>
    );
};

const isSpaceBlockedOccupancyState = (value: OccupancyState): boolean =>
    [OccupancyState.InternalUse, OccupancyState.OutOfOrder, OccupancyState.ReservedLocked].includes(value);
